import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  IconButton,
  Box,
  Typography,
  TextareaAutosize,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText,
} from "@mui/material";
import { IconDelete, IconAdd, IconUpload, IconCancel } from "@/icons";
import isEmail from "validator/lib/isEmail";
import Papa from "papaparse";
import { BatchUserRow, UserRole, UserRowData } from "./BatchUserRow";
import { useSnackbar } from "notistack";
import { RoleChip } from "./RoleChip";

const AVAILABLE_ROLES: UserRole[] = [
  "student",
  "instructor",
  "instructor-team",
];

interface BatchAddUsersDialogProps {
  open: boolean;
  onClose: () => void;
  addUsers: (
    users: {
      email: string;
      roleIds: string[];
      firstName?: string;
      lastName?: string;
      message?: string;
    }[]
  ) => Promise<any>;
}

const createEmptyRow = () => ({
  id: Math.random().toString(36).substring(7),
  email: "",
  roles: [],
});

const CsvFormatDialog = ({ open, onClose, onProceed }: { open: boolean, onClose: () => void, onProceed: () => void }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>CSV File Format</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography variant="body1" gutterBottom>
            Please prepare your CSV file with the following columns:
          </Typography>
          <Typography component="div" variant="body2" sx={{ fontFamily: 'monospace', my: 2, p: 2, bgcolor: 'grey.100' }}>
            email, roles, firstName?, lastName?, message?
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Required columns:</strong>
          </Typography>
          <ul>
            <li><Typography variant="body2">email - Valid email address</Typography></li>
            <li><Typography variant="body2">roles - space-separated list of roles: student instructor instructor-team</Typography></li>
          </ul>
          <Typography variant="body2" gutterBottom>
            <strong>Optional columns:</strong>
          </Typography>
          <ul>
            <li><Typography variant="body2">firstName - User's first name</Typography></li>
            <li><Typography variant="body2">lastName - User's last name</Typography></li>
            <li><Typography variant="body2">message - Welcome message</Typography></li>
          </ul>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Example: john@example.com, student instructor-team, John, Doe, Welcome to the course!
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">Cancel</Button>
        <Button onClick={onProceed} variant="contained">Select CSV File</Button>
      </DialogActions>
    </Dialog>
  );
};

export function BatchAddUsersDialog({
  open,
  onClose,
  addUsers,
}: BatchAddUsersDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<UserRowData[]>([]);
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [pasteContent, setPasteContent] = useState("");
  const [pasteRoles, setPasteRoles] = useState<UserRole[]>([]);
  const [showCsvFormatDialog, setShowCsvFormatDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize with one empty row
  useEffect(() => {
    if (open) {
      setRows([]);
    }
  }, [open]);

  const validRowCount = useMemo(
    () =>
      rows.filter((row) => !row.error && row.email && row.roles.length > 0)
        .length,
    [rows]
  );
  const hasInvalidRows = useMemo(
    () => rows.some((row) => row.error || !row.email || !row.roles.length),
    [rows]
  );

  const handleAddRow = useCallback(() => {
    setRows((prev) => {
      // Check if there's an empty row we can reuse
      const emptyRow = prev.find(
        (row) =>
          !row.email &&
          row.roles.length === 0 &&
          !row.firstName &&
          !row.lastName &&
          !row.message
      );
      if (emptyRow) {
        return prev;
      }
      return [...prev, createEmptyRow()];
    });
  }, []);

  const handleDeleteRow = useCallback((id: string) => {
    setRows((prev) => {
      const newRows = prev.filter((row) => row.id !== id);
      // Always keep at least one row
      if (newRows.length === 0) {
        return [createEmptyRow()];
      }
      return newRows;
    });
  }, []);

  const handleUpdateRow = useCallback(
    (id: string, updates: Partial<UserRowData>) => {
      setRows((prev) =>
        prev.map((row) => {
          if (row.id === id) {
            const updated = { ...row, ...updates };

            // Validate email
            if (updated.email && !isEmail(updated.email)) {
              updated.error = "Invalid email";
            } else {
              // Check for duplicates
              const duplicate = prev.find(
                (r) => r.id !== id && r.email === updated.email
              );
              if (duplicate) {
                updated.error = "Email already exists in the list";
              } else {
                updated.error = undefined;
              }
            }

            return updated;
          }
          return row;
        })
      );
    },
    []
  );

  const handlePasteConfirm = useCallback(() => {
    if (!pasteContent || pasteRoles.length === 0) return;

    const emails = pasteContent
      .split(/[\s,]+/)
      .map((email) => email.trim())
      .filter(Boolean);
    //   .filter((email) => email && isEmail(email));

    setRows((prev) => {
      const newRows = [...prev];

      // Try to reuse empty row if it exists
      const emptyRowIndex = newRows.findIndex(
        (row) =>
          !row.email &&
          row.roles.length === 0 &&
          !row.firstName &&
          !row.lastName &&
          !row.message
      );

      emails.forEach((email, index) => {
        const existingRow = newRows.find((row) => row.email === email);
        if (existingRow) {
          // Merge roles with existing row
          existingRow.roles = [
            ...new Set([...existingRow.roles, ...pasteRoles]),
          ];
        } else if (index === 0 && emptyRowIndex !== -1) {
          // Reuse empty row for first email
          newRows[emptyRowIndex].email = email;
          newRows[emptyRowIndex].roles = [...pasteRoles];
        } else {
          newRows.push({
            id: Math.random().toString(36).substring(7),
            email,
            roles: [...pasteRoles],
            error: isEmail(email) ? undefined : "Invalid email",
          });
        }
      });

      return newRows;
    });

    setPasteDialogOpen(false);
    setPasteContent("");
    setPasteRoles([]);
  }, [pasteContent, pasteRoles]);

  const handleCsvUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      Papa.parse(file, {
        complete: (results: { data: any[][] }) => {
          const invalidRows: { row: number; message: string }[] = [];
          const newRows = results.data
            .filter((row, index): row is string[] => {
              if (row.length < 2) return false;

              // Skip if it looks like a header row
              if (
                index === 0 &&
                row.some(
                  (cell) =>
                    typeof cell === "string" &&
                    [
                      "email",
                      "role",
                      "firstname",
                      "lastname",
                      "message",
                    ].includes(cell.toLowerCase().replace(/[^a-z]/g, ""))
                )
              ) {
                return false;
              }

              const [email, roles] = row;
            //   const roleArray = (roles as string)
            //     .split(",")
            //     .map((r) => r.trim());

            //   if (!isEmail(email)) {
            //     invalidRows.push({
            //       row: index + 1,
            //       message: `Invalid email: ${email}`,
            //     });
            //     return false;
            //   }

            //   if (
            //     roleArray.some((r) => !AVAILABLE_ROLES.includes(r as UserRole))
            //   ) {
            //     invalidRows.push({
            //       row: index + 1,
            //       message: `Invalid roles: "${roles}"`,
            //     });
            //     return false;
            //   }

              return true;
            })
            .map((row) => {
              const [email, roles, firstName, lastName, message] = row;
              const roleArray = (roles as string)
                .split(",")
                .map((r) => r.trim()) as UserRole[];

              return {
                id: Math.random().toString(36).substring(7),
                email,
                roles: roleArray.filter((role) =>
                  AVAILABLE_ROLES.includes(role)
                ),
                firstName,
                lastName,
                message,
                error: isEmail(email) ? undefined : "Invalid email",
              };
            });

        //   if (invalidRows.length > 0) {
        //     enqueueSnackbar(
        //       `${invalidRows.length} invalid rows were skipped. Check the data and try again.`,
        //       { variant: "warning" }
        //     );
        //   }

          setRows((prev) => {
            // Try to reuse empty row if it exists
            const emptyRowIndex = prev.findIndex(
              (row) =>
                !row.email &&
                row.roles.length === 0 &&
                !row.firstName &&
                !row.lastName &&
                !row.message
            );

            const merged = [...prev];

            newRows.forEach((newRow, index) => {
              const existingRow = merged.find(
                (row) => row.email === newRow.email
              );
              if (existingRow) {
                // Merge roles and other fields
                existingRow.roles = [
                  ...new Set([...existingRow.roles, ...newRow.roles]),
                ];
                if (newRow.firstName) existingRow.firstName = newRow.firstName;
                if (newRow.lastName) existingRow.lastName = newRow.lastName;
                if (newRow.message) existingRow.message = newRow.message;
              } else if (index === 0 && emptyRowIndex !== -1) {
                // Reuse empty row for first new row
                merged[emptyRowIndex] = newRow;
              } else {
                merged.push(newRow);
              }
            });

            return merged;
          });
        },
      });

      // Reset file input
      event.target.value = "";
    },
    [enqueueSnackbar]
  );

  const handleCsvFormatDialogProceed = () => {
    setShowCsvFormatDialog(false);
    fileInputRef.current?.click();
  };

  const handleSubmit = useCallback(async () => {
    const validUsers = rows
      .filter((row) => !row.error && row.email && row.roles.length > 0)
      .map(({ email, roles, firstName, lastName, message }) => ({
        email,
        roleIds: roles,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        message: message || undefined,
      }));

    await addUsers(validUsers);
    onClose();
  }, [rows, addUsers, onClose]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Add Multiple Users</DialogTitle>
        <DialogContent>
          <Stack spacing={2} >
            {rows.map((row) => (
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                justifyContent="stretch"
                key={row.id}
                sx={{paddingRight: 3, position: "relative"}}
              >
                <BatchUserRow
                  data={row}
                  availableRoles={AVAILABLE_ROLES}
                  onChange={handleUpdateRow}
                />
                <IconButton
                  size="small"
                  onClick={() => handleDeleteRow(row.id)}
                  sx={{
                    position: "absolute",
                    color: "error.main",
                    right: -12,
                  }}
                >
                  <IconDelete />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} justifyContent="flex-start">
              <Button
                startIcon={<IconAdd />}
                onClick={() => setPasteDialogOpen(true)}
                size="small"
                color="secondary"
              >
                Input Emails
              </Button>
              <Button
                startIcon={<IconUpload />}
                onClick={() => setShowCsvFormatDialog(true)}
                size="small"
                color="secondary"
              >
                Import from CSV
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".csv"
                onChange={handleCsvUpload}
              />
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={onClose} color="secondary">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={validRowCount === 0 || hasInvalidRows}
              >
                Add {validRowCount} User{validRowCount !== 1 ? "s" : ""}
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pasteDialogOpen}
        onClose={() => setPasteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Input Emails</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="caption">
              Input emails separated by spaces, commas, or new lines
            </Typography>
            <TextareaAutosize
              minRows={5}
              style={{
                width: "100%",
                padding: 8,
                fontFamily: "inherit",
              }}
              value={pasteContent}
              onChange={(e) => setPasteContent(e.target.value)}
            />
            <FormControl fullWidth size="small">
              <InputLabel id="paste-roles-label">Assign Roles</InputLabel>
              <Select
                labelId="paste-roles-label"
                multiple
                label="Assign Roles"
                value={pasteRoles}
                onChange={(e) => setPasteRoles(e.target.value as UserRole[])}
                renderValue={(selected) => (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ flexWrap: "wrap", gap: 0.5 }}
                  >
                    {(selected as UserRole[]).map((role) => (
                      <RoleChip
                        key={role}
                        roleId={role}
                        onDelete={() =>
                          setPasteRoles(pasteRoles.filter((r) => r !== role))
                        }
                        deleteIcon={
                          <IconCancel
                            onMouseDown={(event) => event.stopPropagation()}
                          />
                        }
                      />
                    ))}
                  </Stack>
                )}
              >
                {AVAILABLE_ROLES.filter(
                  (role) => !pasteRoles.includes(role)
                ).map((role) => (
                  <MenuItem key={role} value={role}>
                    <RoleChip key={role} roleId={role} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasteDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePasteConfirm}
            disabled={!pasteContent.trim() || pasteRoles.length === 0}
          >
            Add Emails
          </Button>
        </DialogActions>
      </Dialog>

      <CsvFormatDialog
        open={showCsvFormatDialog}
        onClose={() => setShowCsvFormatDialog(false)}
        onProceed={handleCsvFormatDialogProceed}
      />
    </>
  );
}
